import { AfterViewInit, Component, ElementRef, HostBinding, Input, OnDestroy, ViewChild } from '@angular/core';

export interface ZebraFloatingDecor {
    src: string;
    alt?: string;
    className?: string;
}

@Component({
    selector: 'app-text-img-zebra',
    templateUrl: './text-img-zebra.component.html',
    styleUrls: ['./text-img-zebra.component.scss']
})
export class TextImgZebraComponent implements AfterViewInit, OnDestroy {
    @Input() sectionId = '';
    @Input() imageSrc = '';
    @Input() imageAlt = '';
    @Input() title = '';
    @Input() subtitle = '';
    @Input() copy = '';
    @Input() paragraphs: string[] = [];
    @Input() boldParagraphIndexes: number[] = [];
    @Input() reverse = false;
    @Input() withBackground = false;
    @Input() decorVariantClass = '';
    @Input() topDecorImageSrc = '';
    @Input() topDecorImageAlt = '';
    @Input() topDecorClass = '';
    @Input() bottomDecorImageSrc = '';
    @Input() bottomDecorImageAlt = '';
    @Input() bottomDecorClass = '';
    @Input() floatingDecors: ZebraFloatingDecor[] = [];
    @Input() enableScrollMotion = false;
    @Input() scrollTranslateIntensity = 14;
    @Input() scrollRotateIntensity = 4;
    @HostBinding('class.is-in-view') isInView = false;
    @ViewChild('zebraSection') zebraSection?: ElementRef<HTMLElement>;
    private observer?: IntersectionObserver;
    private ticking = false;
    private readonly onWindowScroll = () => this.scheduleMotionUpdate();
    private readonly onWindowResize = () => this.scheduleMotionUpdate();

    constructor(private readonly elementRef: ElementRef<HTMLElement>) {}

    ngAfterViewInit(): void {
        this.observer = new IntersectionObserver(([entry]) => {
            if (entry?.isIntersecting) {
                this.isInView = true;
            }
        }, {
            threshold: 0.35,
            rootMargin: '0px 0px -10% 0px'
        });
        this.observer.observe(this.elementRef.nativeElement);

        if (this.enableScrollMotion && typeof window !== 'undefined') {
            window.addEventListener('scroll', this.onWindowScroll, { passive: true });
            window.addEventListener('resize', this.onWindowResize, { passive: true });
            this.updateScrollMotionProgress();
        }
    }

    ngOnDestroy(): void {
        this.observer?.disconnect();
        if (typeof window !== 'undefined') {
            window.removeEventListener('scroll', this.onWindowScroll);
            window.removeEventListener('resize', this.onWindowResize);
        }
    }

    private scheduleMotionUpdate(): void {
        if (!this.enableScrollMotion || this.ticking) {
            return;
        }
        this.ticking = true;
        requestAnimationFrame(() => {
            this.updateScrollMotionProgress();
            this.ticking = false;
        });
    }

    private updateScrollMotionProgress(): void {
        const section = this.zebraSection?.nativeElement;
        if (!section || typeof window === 'undefined') {
            return;
        }

        const rect = section.getBoundingClientRect();
        const viewportCenter = window.innerHeight / 2;
        const sectionCenter = rect.top + rect.height / 2;
        const rawProgress = (viewportCenter - sectionCenter) / window.innerHeight;
        const progress = Math.max(-1, Math.min(1, rawProgress));

        section.style.setProperty('--scroll-progress', progress.toFixed(4));
        section.style.setProperty('--motion-shift', `${this.scrollTranslateIntensity}px`);
        section.style.setProperty('--motion-rotate', `${this.scrollRotateIntensity}deg`);
    }

}
